'use client'
import { useCallback, useRef, useState } from 'react'

// Shared validation + submit logic for the homepage and /contact forms.
// Both forms post { name, email, message, recaptchaToken } to /api/contact.

export const FALLBACK_EMAIL = 'info@mallardtech.com'
export const SEND_ERROR_MESSAGE = `Couldn't send. Email us at ${FALLBACK_EMAIL} instead.`

const FIELDS = ['name', 'email', 'message']
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

// Returns an error string for one field, or '' when the value is fine
function validateField(field, rawValue) {
    const value = String(rawValue ?? '').trim()

    if (field === 'name') {
        if (!value) {return 'Enter your name.'}
        if (value.length > 100) {return 'Keep your name under 100 characters.'}
    }

    if (field === 'email') {
        if (!value) {return 'Enter your email so we can reply.'}
        if (!EMAIL_PATTERN.test(value) || value.length > 100) {
            return 'Enter a valid email, like you@company.com.'
        }
    }

    if (field === 'message') {
        if (!value) {return "Tell us a little about what's slowing you down."}
        if (value.length > 5000) {return 'Keep the message under 5,000 characters.'}
    }

    return ''
}

export default function useContactForm({ executeRecaptcha, idPrefix = 'contact' }) {
    const [values, setValues] = useState({ name: '', email: '', message: '' })
    const [errors, setErrors] = useState({})
    // 'idle' | 'submitting' | 'success' | 'error'
    const [status, setStatus] = useState('idle')
    const [statusMessage, setStatusMessage] = useState('')
    const fieldRefs = useRef({})

    const setFieldError = (field, message) =>
        setErrors((prev) => ({ ...prev, [field]: message }))

    const handleChange = (field) => (event) => {
        const { value } = event.target
        setValues((prev) => ({ ...prev, [field]: value }))
        // Once a field has shown an error, re-check it live so the error clears as soon as it's fixed
        if (errors[field]) {
            setFieldError(field, validateField(field, value))
        }
    }

    const handleBlur = (field) => (event) => {
        setFieldError(field, validateField(field, event.target.value))
    }

    // Spread onto an <input>/<textarea>: wires value, validation and a11y attributes
    const getFieldProps = (field) => {
        const id = `${idPrefix}-${field}`
        const error = errors[field]
        return {
            id,
            value: values[field],
            onChange: handleChange(field),
            onBlur: handleBlur(field),
            ref: (node) => {
                fieldRefs.current[field] = node
            },
            required: true,
            'aria-required': 'true',
            'aria-invalid': error ? 'true' : 'false',
            'aria-describedby': error ? `${id}-error` : undefined,
        }
    }

    const getErrorId = (field) => `${idPrefix}-${field}-error`

    const handleSubmit = async (event) => {
        event.preventDefault()
        if (status === 'submitting') {return}

        // Validate everything, then focus the first invalid field
        const nextErrors = {}
        FIELDS.forEach((field) => {
            nextErrors[field] = validateField(field, values[field])
        })
        setErrors(nextErrors)

        const firstInvalid = FIELDS.find((field) => nextErrors[field])
        if (firstInvalid) {
            fieldRefs.current[firstInvalid]?.focus()
            setStatus('idle')
            setStatusMessage('')
            return
        }

        // reCAPTCHA v3 script not loaded (blocked, offline, or missing key): say so instead of doing nothing
        if (typeof executeRecaptcha !== 'function') {
            setStatus('error')
            setStatusMessage(SEND_ERROR_MESSAGE)
            return
        }

        setStatus('submitting')
        setStatusMessage('Sending your message...')

        try {
            const token = await executeRecaptcha('contact_submit')
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: values.name.trim(),
                    email: values.email.trim(),
                    message: values.message.trim(),
                    recaptchaToken: token,
                }),
            })

            if (!response.ok) {
                throw new Error('Contact request failed')
            }

            setStatus('success')
            setStatusMessage('Message sent. We will reply by email.')
            setValues({ name: '', email: '', message: '' })
            setErrors({})
        } catch (error) {
            console.error('Error:', error)
            setStatus('error')
            setStatusMessage(SEND_ERROR_MESSAGE)
        }
    }

    const reset = useCallback(() => {
        setStatus('idle')
        setStatusMessage('')
    }, [])

    return {
        values,
        errors,
        status,
        statusMessage,
        isSubmitting: status === 'submitting',
        getFieldProps,
        getErrorId,
        handleSubmit,
        reset,
    }
}
